import React from 'react';
import PropTypes from 'prop-types';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import './styles.scss';
import { THEME } from '../../common/constants';
import CustomizedAxisTick from './CustomTick';

function Chart({
    width,
    height,
    data,
    radius,
    xLabel,
    yLabel,
    barSize,
    useCustomTick,
    isMultiColor,
    yAxiesProps,
    xAxisProps,
    chartTheme,
}) {
    const Tick = useCustomTick
        ? CustomizedAxisTick
        : { fill: THEME.TEXT_GRAAY };

    return (
        <div className="charts-wrapper">
            <ResponsiveContainer>
                <BarChart width={width} height={height} data={data}>
                    <CartesianGrid
                        stroke={THEME.LIGHT_GRAY}
                        strokeWidth={5}
                        vertical={false}
                        horizontal
                        horizontalFill={
                            chartTheme && chartTheme.fill
                                ? [chartTheme.fill]
                                : ['white']
                        }
                        fillOpacity={
                            chartTheme && chartTheme.fillOpacity
                                ? chartTheme.fillOpacity
                                : 0.4
                        }
                    />
                    <XAxis
                        dataKey={xLabel}
                        tick={useCustomTick ? <Tick /> : Tick}
                        stroke={'white'}
                        interval={0} // remove gap between x axis labels to prevent them disappearing in responsive
                        {...xAxisProps}
                    />
                    <YAxis
                        tick={{ fill: THEME.TEXT_GRAY }}
                        stroke={'white'}
                        {...yAxiesProps}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: 'black',
                            borderRadius: 30,
                            fontSize: 12,
                            height: 25,
                            lineHeight: '5px',
                            paddingTop: 5,
                            paddingBottom: 5,
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                        }} // tooltip shape and color
                        itemStyle={{ color: 'white' }} // tooltip text color
                        cursor={{ fill: 'transparent' }} // remove custom bar highlight
                        labelFormatter={() => {
                            return ''; // remove month label from tooltip by returning empty string
                        }}
                    />
                    {isMultiColor ? (
                        <Bar
                            isAnimationActive={false}
                            radius={radius}
                            dataKey={yLabel}
                            fill={THEME.PRIMARY_BLUE}
                            barSize={barSize}
                        >
                            {data.map(({ color, id }) => {
                                return <Cell key={`cell_${id}`} fill={color} />;
                            })}
                        </Bar>
                    ) : (
                        <Bar
                            isAnimationActive={false}
                            radius={radius}
                            dataKey={yLabel}
                            fill={THEME.PRIMARY_BLUE}
                            barSize={barSize}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

Chart.propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    radius: PropTypes.number,
    barSize: PropTypes.number,
    data: PropTypes.arrayOf(PropTypes.object),
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    useCustomTick: PropTypes.bool,
    isMultiColor: PropTypes.bool,
    xAxisProps: PropTypes.object,
    yAxiesProps: PropTypes.object,
    chartTheme: PropTypes.object,
};

Chart.defaultProps = {
    width: 400,
    height: 400,
    radius: 5,
    barSize: 12,
    data: [],
    xLabel: 'month',
    yLabel: 'litterCount',
    useCustomTick: false,
    isMultiColor: false,
    xAxisProps: {},
    yAxiesProps: {},
    chartTheme: null,
};

export default Chart;
